import { env } from "configs/EnvironmentConfig";
import ticketService from "services/ticketService";

export function buildPublicResourceFileUrl(filePath) {
  if (!filePath || typeof filePath !== "string") {
    return "";
  }
  const trimmed = filePath.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  const base = env.API_MSP || "";
  if (!base) {
    return trimmed;
  }
  try {
    const baseNorm = base.endsWith("/") ? base : `${base}/`;
    const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return new URL(path, baseNorm).href;
  } catch {
    const b = base.replace(/\/$/, "");
    const p = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return `${b}${p}`;
  }
}

export function resolveTicketImageSrc(entry) {
  if (entry == null) {
    return "";
  }
  if (typeof entry === "string") {
    return buildPublicResourceFileUrl(entry) || entry;
  }
  if (typeof entry === "object" && entry.file_path) {
    return buildPublicResourceFileUrl(entry.file_path);
  }
  return "";
}

export function getTicketImageResourceId(entry) {
  if (entry && typeof entry === "object" && entry.id != null) {
    return entry.id;
  }
  return null;
}

export function getTicketRowImages(row) {
  const resources = row?.resources;

  return Array.isArray(resources) ? resources : [];
}

function mimeFromMineType(mine) {
  if (!mine || typeof mine !== "string") {
    return "image/jpeg";
  }
  const m = mine.toLowerCase().trim();
  if (m.startsWith("image/")) {
    return m;
  }
  if (m === "jpg" || m === "jpeg") {
    return "image/jpeg";
  }
  if (m === "png" || m === "gif" || m === "webp") {
    return `image/${m}`;
  }
  return "image/jpeg";
}

function findBase64StringDeep(val, depth = 0) {
  if (depth > 10 || val == null) {
    return "";
  }
  if (typeof val === "string") {
    const t = val.replace(/\s/g, "");
    if (t.length < 32) {
      return "";
    }
    if (/^[A-Za-z0-9+/]+=*$/.test(t) && t.length % 4 === 0) {
      return t;
    }
    if (t.length > 64 && /^[A-Za-z0-9+/=]+$/.test(t)) {
      return t;
    }
    return "";
  }
  if (Array.isArray(val)) {
    for (let i = 0; i < val.length; i += 1) {
      const f = findBase64StringDeep(val[i], depth + 1);
      if (f) {
        return f;
      }
    }
    return "";
  }
  if (typeof val === "object") {
    const keys = Object.keys(val);
    for (let i = 0; i < keys.length; i += 1) {
      const f = findBase64StringDeep(val[keys[i]], depth + 1);
      if (f) {
        return f;
      }
    }
  }
  return "";
}

function pickPreviewPayloadFromJson(j, resource) {
  if (j == null || typeof j !== "object") {
    return "";
  }
  const nested =
    typeof j.data === "object" && j.data !== null && !Array.isArray(j.data)
      ? j.data
      : null;
  const inner =
    j.preview ??
    j.response ??
    j.content ??
    j.result ??
    (typeof j.data === "string" ? j.data : null) ??
    nested?.preview ??
    nested?.data ??
    nested?.base64 ??
    nested?.file ??
    nested?.content ??
    nested?.image ??
    nested?.fileContent;
  if (typeof inner === "string" && inner.length > 0) {
    const t = inner.trim();
    if (t.startsWith("data:") || /^https?:\/\//i.test(t)) {
      return t;
    }
    if (t.length > 20) {
      return `data:${mimeFromMineType(resource?.mine_type)};base64,${t}`;
    }
  }
  const deep = findBase64StringDeep(j);
  if (deep) {
    return `data:${mimeFromMineType(resource?.mine_type)};base64,${deep}`;
  }
  return "";
}

function stripUtf8Bom(bytes) {
  if (
    bytes.length >= 3 &&
    bytes[0] === 0xef &&
    bytes[1] === 0xbb &&
    bytes[2] === 0xbf
  ) {
    return bytes.subarray(3);
  }
  return bytes;
}

function sniffImageMime(bytes) {
  if (bytes.length < 4) {
    return "";
  }
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "image/jpeg";
  }
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return "image/png";
  }
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return "image/gif";
  }
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return "image/webp";
  }
  return "";
}

function skipLeadingSpace(bytes) {
  let start = 0;
  while (
    start < bytes.length &&
    (bytes[start] === 0x20 ||
      bytes[start] === 0x0a ||
      bytes[start] === 0x0d ||
      bytes[start] === 0x09)
  ) {
    start += 1;
  }
  return bytes.subarray(start);
}

async function arrayBufferToImageSrc(ab, resource) {
  const raw = new Uint8Array(ab);
  const bytes = skipLeadingSpace(stripUtf8Bom(raw));
  if (bytes.length === 0) {
    return "";
  }
  if (bytes[0] === 0x7b) {
    const text = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    const t = text.trim();
    try {
      const j = JSON.parse(t);
      const fromJson = pickPreviewPayloadFromJson(j, resource);
      if (fromJson) {
        return fromJson;
      }
    } catch {
    }
    const fromString = await previewBodyToImageSrc(text, resource);
    if (fromString) {
      return fromString;
    }
  }
  const sniffed = sniffImageMime(bytes);
  const mime = sniffed || mimeFromMineType(resource?.mine_type);
  return URL.createObjectURL(new Blob([bytes], { type: mime }));
}

export async function previewBodyToImageSrc(body, resource) {
  if (body == null || body === "") {
    return "";
  }
  if (body instanceof ArrayBuffer) {
    return arrayBufferToImageSrc(body, resource);
  }
  if (typeof Blob !== "undefined" && body instanceof Blob) {
    if (body.size === 0) {
      return "";
    }
    return arrayBufferToImageSrc(await body.arrayBuffer(), resource);
  }
  if (
    typeof body === "object" &&
    body !== null &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer)
  ) {
    const fromJson = pickPreviewPayloadFromJson(body, resource);
    if (fromJson) {
      return fromJson;
    }
  }
  if (typeof body === "string") {
    const t = body.trim();
    if (t.startsWith("data:") || /^https?:\/\//i.test(t)) {
      return t;
    }
    try {
      const j = JSON.parse(t);
      const fromJson = pickPreviewPayloadFromJson(j, resource);
      if (fromJson) {
        return fromJson;
      }
    } catch {
    }
    if (t.length > 80) {
      return `data:${mimeFromMineType(resource?.mine_type)};base64,${t}`;
    }
    return "";
  }
  return "";
}


export async function fetchTicketPreviewImageSrc(resourceId, resource) {
  if (resourceId == null || resourceId === "") {
    return "";
  }
  try {
    const res = await ticketService.getTicketResourcePreviewJson({
      resourceId,
    });
    const url = await previewBodyToImageSrc(res?.data, resource);
    if (url) {
      return url;
    }
  } catch {
  }
  try {
    const res = await ticketService.getTicketResourcePreviewArrayBuffer({
      resourceId,
    });
    const url = await previewBodyToImageSrc(res?.data, resource);
    if (url) {
      return url;
    }
  } catch {
  }
  return "";
}
